import React, { useState, useEffect, useMemo } from "react";
import { Box, useTheme, Typography, IconButton, Button } from "@mui/material";
import { Visibility as VisibilityIcon, Sms as SmsIcon } from "@mui/icons-material";
import { tokens } from "../../theme";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useAccountantService from "../../api/services/AccountantService";
import LoadingSpinner from "../../components/LoadingSpinner";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { toast } from "react-hot-toast";

/** =========================
 * Helpers
 * ========================= */
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "" || value === "null") return "0.00";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return "0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

const isEmptyValue = (value) => value === null || value === undefined || value === "" || value === "null";

const getDisplayValue = (value) => {
  if (isEmptyValue(value)) return "-";
  return formatCurrency(value);
};

/** =========================
 * LocalStorage (persist SENT)
 * ========================= */
// Store SENT state per (termId + classId) so it survives page reload
const smsSentStorageKey = (termId, classId) => `accounts_sms_sent_v1:${termId || "NA"}:${classId || "NA"}`;

const loadSentStatus = (termId, classId) => {
  try {
    const raw = localStorage.getItem(smsSentStorageKey(termId, classId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const saveSentStatus = (termId, classId, statusObj) => {
  try {
    localStorage.setItem(smsSentStorageKey(termId, classId), JSON.stringify(statusObj || {}));
  } catch (e) {
    // ignore storage errors
  }
};

const ClassStudentsAccountsData = () => {
  const { classId, termId } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const { getClassStudentsAccountantDataByTermIdAndClassId, sendSmsAboutAccountsDataToParents, isLoading } =
    useAccountantService();

  const [accountsData, setAccountsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [termName, setTermName] = useState(location.state?.termName || "");
  const [className, setClassName] = useState("");

  // sendingStatus: studentId -> boolean (spinner/disable while sending)
  const [sendingStatus, setSendingStatus] = useState({});
  // sentStatus: studentId -> boolean (persisted to localStorage)
  const [sentStatus, setSentStatus] = useState({});

  // Load persisted SENT status for this class+term
  useEffect(() => {
    if (classId && termId) {
      const stored = loadSentStatus(termId, classId);
      setSentStatus(stored);
    }
  }, [classId, termId]);

  useEffect(() => {
    const fetchData = async () => {
      if (classId && termId) {
        setLoading(true);
        try {
          const data = await getClassStudentsAccountantDataByTermIdAndClassId(termId, classId);

          const accountsArray = Array.isArray(data) ? data : [];

          // Use only debt_balance from database (no frontend calculation)
          const enrichedData = accountsArray.map((row) => {
            const raw = row.debtBalance ?? row.debt_balance ?? null;
            const num = raw != null && raw !== "" ? parseFloat(raw) : NaN;
            const balance = typeof num === "number" && !isNaN(num) ? num : 0;
            return { ...row, balance, debtBalance: raw != null && raw !== "" ? String(raw) : "" };
          });

          setAccountsData(enrichedData);

          if (accountsArray.length > 0) {
            if (accountsArray[0].termName && !termName) setTermName(accountsArray[0].termName);
            if (accountsArray[0].className && !className) setClassName(accountsArray[0].className);
          }
        } catch (error) {
          console.error("Error fetching accounts data:", error);
          toast.error("Failed to fetch accounts data");
          setAccountsData([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, termId]);

  const handleViewDetails = (accountantId) => {
    if (accountantId) {
      navigate(`/view-student-accountant-details/${accountantId}`);
    } else {
      toast.error("Cannot view: Accountant record ID is missing");
    }
  };

  const handleSendSms = async (row) => {
    const studentId = row.studentId;
    if (!studentId) {
      toast.error("Student ID is missing");
      return;
    }

    setSendingStatus((prev) => ({ ...prev, [studentId]: true }));

    try {
      const accountData = {};

      const isNonZero = (value) => {
        if (isEmptyValue(value)) return false;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue !== 0;
      };

      // Add fields only if they have non-zero values
      if (isNonZero(row.firstTermDebt)) accountData.firstTermDebt = parseFloat(row.firstTermDebt);
      if (isNonZero(row.secondTermDebt)) accountData.secondTermDebt = parseFloat(row.secondTermDebt);
      if (isNonZero(row.firstTermFee)) accountData.firstTermFee = parseFloat(row.firstTermFee);
      if (isNonZero(row.secondTermFee)) accountData.secondTermFee = parseFloat(row.secondTermFee);
      if (isNonZero(row.firstTermExamFee)) accountData.firstTermExamFee = parseFloat(row.firstTermExamFee);
      if (isNonZero(row.secondTermExamFee)) accountData.secondTermExamFee = parseFloat(row.secondTermExamFee);
      if (isNonZero(row.firstTermOtherContribution))
        accountData.firstTermOtherContribution = parseFloat(row.firstTermOtherContribution);
      if (isNonZero(row.secondTermOtherContribution))
        accountData.secondTermOtherContribution = parseFloat(row.secondTermOtherContribution);
      if (isNonZero(row.firstTermParentFee)) accountData.firstTermParentFee = parseFloat(row.firstTermParentFee);
      if (isNonZero(row.secondTermParentFee)) accountData.secondTermParentFee = parseFloat(row.secondTermParentFee);
      if (isNonZero(row.firstTermPaidAmount)) accountData.firstTermPaidAmount = parseFloat(row.firstTermPaidAmount);
      if (isNonZero(row.secondTermPaidAmount)) accountData.secondTermPaidAmount = parseFloat(row.secondTermPaidAmount);

      // Use only debt_balance from database (no calculation)
      const raw = row.debtBalance ?? row.debt_balance ?? row.balance;
      const n = raw != null && raw !== "" ? (typeof raw === "number" ? raw : parseFloat(raw)) : NaN;
      accountData.balance = typeof n === "number" && !isNaN(n) ? n : 0;
      accountData.studentName = row.studentName;
      accountData.termName = termName;

      await sendSmsAboutAccountsDataToParents(studentId, accountData);

      // Mark as SENT (persist to localStorage)
      setSentStatus((prev) => {
        const updated = { ...prev, [studentId]: true };
        saveSentStatus(termId, classId, updated);
        return updated;
      });

      toast.success("SMS sent successfully");
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast.error("Failed to send SMS to parents");
    } finally {
      setSendingStatus((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "studentName",
        headerName: "Student Name",
        flex: 1,
        minWidth: 120,
        sortable: true,
        renderCell: (params) => {
          if (!params || !params.row) return "-";
          const name = params.row.studentName || "";
          return name.trim() || "-";
        },
      },
      {
        field: "firstTermDebt",
        headerName: "T-1 Debt",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.firstTermDebt)),
      },
      {
        field: "secondTermDebt",
        headerName: "T-2 Debt",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.secondTermDebt)),
      },
      {
        field: "firstTermFee",
        headerName: "T-1 Fee",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.firstTermFee)),
      },
      {
        field: "secondTermFee",
        headerName: "T-2 Fee",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.secondTermFee)),
      },
      {
        field: "firstTermExamFee",
        headerName: "T-1 Exam Fee",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.firstTermExamFee)),
      },
      {
        field: "secondTermExamFee",
        headerName: "T-2 Exam Fee",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.secondTermExamFee)),
      },
      {
        field: "firstTermOtherContribution",
        headerName: "T-1 Other Contribution",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.firstTermOtherContribution)),
      },
      {
        field: "secondTermOtherContribution",
        headerName: "T-2-Other-Contribution",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.secondTermOtherContribution)),
      },
      {
        field: "firstTermParentFee",
        headerName: "T-1-ParentFee",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.firstTermParentFee)),
      },
      {
        field: "secondTermParentFee",
        headerName: "T-2 Parent Fee",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.secondTermParentFee)),
      },
      {
        field: "firstTermPaidAmount",
        headerName: "T-1-PaidAmount",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.firstTermPaidAmount)),
      },
      {
        field: "secondTermPaidAmount",
        headerName: "T-2-PaidAmount",
        flex: 1,
        minWidth: 80,
        renderCell: (params) => (!params?.row ? "-" : getDisplayValue(params.row.secondTermPaidAmount)),
      },
      {
        field: "balance",
        headerName: "Debt_Balance",
        flex: 1,
        minWidth: 90,
        type: "number",
        renderCell: (params) => {
          if (!params?.row) return "-";
          const b = params.row.balance ?? params.row.debtBalance ?? params.row.debt_balance;
          const numBalance = b != null && b !== "" ? (typeof b === "number" ? b : parseFloat(b)) : NaN;
          const num = typeof numBalance === "number" && !isNaN(numBalance) ? numBalance : 0;
          return (
            <Typography
              sx={{
                color: num > 0 ? colors.redAccent[500] : num < 0 ? colors.greenAccent[500] : "inherit",
                fontWeight: "bold",
              }}
            >
              {getDisplayValue(num !== 0 ? String(num) : null)}
            </Typography>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 220,
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: (params) => {
          if (!params?.row) return null;

          const studentId = params.row.studentId;
          const isSending = !!sendingStatus[studentId];
          const isSent = !!sentStatus[studentId];

          return (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={() => handleViewDetails(params.row.id)}
                color="info"
                title="View Account Details"
              >
                <VisibilityIcon />
              </IconButton>

              {/* ✅ Button changes:
                  - SEND SMS -> SENT after success
                  - Green when SENT
                  - Click again still sends
                  - Persist after reload (localStorage) */}
              <Button
                size="small"
                variant="contained"
                startIcon={<SmsIcon />}
                onClick={() => handleSendSms(params.row)}
                disabled={isSending}
                sx={{
                  backgroundColor: isSent ? colors.greenAccent[500] : colors.blueAccent[500],
                  "&:hover": {
                    backgroundColor: isSent ? colors.greenAccent[600] : colors.blueAccent[600],
                  },
                  "&:disabled": {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[300],
                  },
                  color: "white",
                  minWidth: "120px",
                  fontWeight: 700,
                }}
              >
                {isSending ? "Sending..." : isSent ? "SENT" : "SEND SMS"}
              </Button>
            </Box>
          );
        },
      },
    ],
    [colors, sendingStatus, sentStatus] // keep deps
  );

  // Total term class debt: sum of debt_balance from database per row (no calculation)
  const totalClassDebtBalance = useMemo(() => {
    if (!accountsData || accountsData.length === 0) return 0;
    return accountsData.reduce((sum, row) => {
      const b = row.balance ?? row.debtBalance ?? row.debt_balance;
      const num = b != null && b !== "" ? (typeof b === "number" ? b : parseFloat(b)) : NaN;
      return sum + (typeof num === "number" && !isNaN(num) ? num : 0);
    }, 0);
  }, [accountsData]);

  return (
    <Box sx={{ ml: "20px", mr: "20px" }}>
      {/* Title */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Box>
          <Typography variant="h4" align="start" gutterBottom>
            ACCOUNTS DATA FOR {termName ? termName.toUpperCase() : "TERM"} - {className ? className.toUpperCase() : "CLASS"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Total Students: {accountsData.length}
          </Typography>
          {!loading && !isLoading && accountsData.length > 0 && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
              Total Term Class Debt: {formatCurrency(totalClassDebtBalance)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Data Grid */}
      {loading || isLoading ? (
        <LoadingSpinner message="Loading accounts data..." height="400px" />
      ) : accountsData.length > 0 ? (
        <Box
          height="100vh"
          sx={{
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.grey[100]} !important`,
            },
            "& .MuiDataGrid-cell:hover": {
              cursor: "default",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid rgba(224, 224, 224, 1)",
            },
          }}
        >
          <DataGrid
            disableRowSelectionOnClick
            rows={accountsData}
            columns={columns}
            getRowId={(row) => row.id}
            pagination={false}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
          />
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No accounts data available for this class and term.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClassStudentsAccountsData;
